import Card from "@mui/material/Card";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import { useState, useEffect } from "react";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import { Grid, MenuItem, Chip, Autocomplete, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import api from "../../../utils/api";
import dayjs from "dayjs";
import CustomDatePicker from "components/CustomDatePicker/CustomDatePicker";

function EditDemo() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [slots, setSlots] = useState([]);
  const [statusSaving, setStatusSaving] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState({});

  const [formData, setFormData] = useState({
    studentIds: [],
    tutorId: "",
    tutorByAdmin: "",
    preferredDate: null, // Will be set to dayjs object when data loads
    initialPreferredTimeSlots: [], // Read-only slots from creation
    preferredTimeSlots: [], // Final slot
    demoType: "ONLINE",
    demoStatus: "REQUESTED",
    notes: "",
    address: "",
    rejectReason: "",
    meetingLink: "",
    scheduledDate: null, // Will be set to dayjs object when data loads
    perSessionFee: 0,
    monthlyFee: "",
  });

  const [errors, setErrors] = useState({});

  // Helper: extract messages from server/client payloads and show toasts
  const showToastsFromResponse = (payload, type = "error") => {
    try {
      const messages = [];
      const push = (m) => {
        if (m && typeof m === "string") messages.push(m);
      };

      if (!payload) return;

      // Axios error object
      if (payload?.response?.data) {
        return showToastsFromResponse(payload.response.data, type);
      }

      // Direct data object
      push(payload.message);
      push(payload.msg);
      push(payload.error);

      // Array of validation errors (strings or objects)
      if (Array.isArray(payload.errors)) {
        payload.errors.forEach((e) => push(typeof e === "string" ? e : e?.message));
      }

      // Mongoose formatted error
      if (payload.mongooseError) {
        push(payload.mongooseError.message);
        if (Array.isArray(payload.mongooseError.details)) {
          payload.mongooseError.details.forEach((d) => push(d?.message));
        }
      }

      // Fallbacks
      if (messages.length === 0 && typeof payload === "string") {
        push(payload);
      }
      if (messages.length === 0) {
        push(type === "success" ? "Success" : "Unexpected error");
      }

      messages.forEach((m) => {
        if (type === "success") toast.success(m);
        else if (type === "info") toast.info(m);
        else toast.error(m);
      });
    } catch (_) {
      if (type === "success") toast.success("Success");
      else toast.error("Something went wrong");
    }
  };

  // Fetch required data and demo details
  useEffect(() => {
    setCurrentUser(JSON.parse(localStorage.getItem("user") || "{}"));
    Promise.all([fetchDemoDetails(), fetchTutors(), fetchSlots()]).finally(() => {
      setInitialLoading(false);
    });
  }, [id]);

  const fetchDemoDetails = async () => {
    try {
      const response = await api.get(`/demo/${id}`);

      if (response.data.success && response.data.data) {
        // getDemoById returns { data: { demo } }
        const demo = response.data.data.demo || response.data.data;
        // helper to format ISO to "YYYY-MM-DDTHH:mm" in local time for datetime-local input
        const toLocalDateTimeInput = (iso) => {
          try {
            const d = new Date(iso);
            if (isNaN(d.getTime())) return "";
            const pad = (n) => String(n).padStart(2, "0");
            const yyyy = d.getFullYear();
            const mm = pad(d.getMonth() + 1);
            const dd = pad(d.getDate());
            const hh = pad(d.getHours());
            const min = pad(d.getMinutes());
            return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
          } catch (_) {
            return "";
          }
        };

        // Build address from parent's city and area if demo address is empty
        let demoAddress = demo?.address || "";
        if (!demoAddress && demo?.parentId) {
          const parentCity = demo.parentId?.city || "";
          const parentArea = demo.parentId?.area || "";
          if (parentCity || parentArea) {
            demoAddress = [parentArea, parentCity].filter(Boolean).join(", ");
          }
        }

        setFormData({
          studentIds: Array.isArray(demo?.students)
            ? demo.students.map((s) => s?._id || s?.toString() || s)
            : demo?.tutorByStudent
            ? [demo.tutorByStudent?._id || demo.tutorByStudent]
            : [],
          // Prefer the originally requested tutor (Tutor model) over admin user id (User model)
          tutorId:
            (demo?.meetingDetails?.tutorId &&
              (demo.meetingDetails.tutorId?.toString?.() || `${demo.meetingDetails.tutorId}`)) ||
            (demo?.tutorByAdmin?._id &&
              (demo.tutorByAdmin._id?.toString?.() || `${demo.tutorByAdmin._id}`)) ||
            "",
          tutorByAdmin:
            (demo?.tutorByAdmin?._id &&
              (demo.tutorByAdmin._id?.toString?.() || `${demo.tutorByAdmin._id}`)) ||
            (typeof demo?.tutorByAdmin === "string"
              ? demo.tutorByAdmin
              : (() => {
                  // Get current admin from localStorage
                  try {
                    const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
                    return currentUser?._id || currentUser?.id || "";
                  } catch (e) {
                    return "";
                  }
                })()),
          preferredDate: demo?.demoDate ? dayjs(demo.demoDate) : null,
          initialPreferredTimeSlots: Array.isArray(demo?.preferedTimeSlots)
            ? demo.preferedTimeSlots
            : [],
          preferredTimeSlots: Array.isArray(demo?.meetingDetails?.preferredTimeSlots)
            ? demo.meetingDetails.preferredTimeSlots.map((s) => s?.toString?.() || `${s}`)
            : [],
          demoType: demo?.demoType ? demo.demoType.toUpperCase() : "ONLINE",
          demoStatus: demo?.demoStatus ? demo.demoStatus.toUpperCase() : "REQUESTED",
          notes: demo?.meetingDetails?.notes || "",
          address: demoAddress,
          rejectReason: demo?.rejectReason || "",
          meetingLink: demo?.meetingDetails?.meetingLink || "",
          scheduledDate: demo?.meetingDetails?.scheduledDateTime
            ? dayjs(demo.meetingDetails.scheduledDateTime)
            : null,
          perSessionFee: demo?.perSessionFee || 0,
          monthlyFee:
            demo?.monthlyFee !== null && demo?.monthlyFee !== undefined
              ? String(demo.monthlyFee)
              : "",
        });

        // Fetch students for this demo's parent
        const parentId = demo?.tutorByStudent?.parentId || demo?.parentId?._id || demo?.parentId;
        if (parentId) {
          await fetchStudents(parentId);
        }
      }
    } catch (error) {
      console.error("Error fetching demo details:", error);
      showToastsFromResponse(error);
    }
  };

  const fetchStudents = async (parentId = null) => {
    try {
      const params = parentId ? { parentId } : {};
      const response = await api.get("/student", { params });
      setStudents(Array.isArray(response.data.data) ? response.data.data : []);
    } catch (error) {
      console.error("Error fetching students:", error);
      setStudents([]);
      showToastsFromResponse(error);
    }
  };

  const fetchTutors = async () => {
    try {
      const response = await api.get("/tutor");
      const tutorsData = response.data.data?.tutors || response.data.data || [];
      const normalized = (Array.isArray(tutorsData) ? tutorsData : []).map((t) => ({
        ...t,
        _id: t?._id || t?.id || t?.Id || t?._Id || undefined,
      }));
      setTutors([{ _id: "", fullName: "Not Assigned", phone: "" }, ...normalized]);
    } catch (error) {
      console.error("Error fetching tutors:", error);
      setTutors([]);
      showToastsFromResponse(error);
    }
  };

  // Fallback: If specific tutorId isn't in the current list, fetch it and append
  const fetchTutorById = async (tid) => {
    try {
      if (!tid) return;
      const res = await api.get(`/tutor`, { params: { id: tid } });
      const t = res?.data?.data;
      if (t && (t._id || t.id)) {
        setTutors((prev) => {
          const exists = prev?.some(
            (x) => (x?._id || x?.id)?.toString() === (t?._id || t?.id)?.toString()
          );
          if (exists) return prev;
          return [...prev, { ...t, _id: t?._id || t?.id }];
        });
      }
    } catch (e) {
      console.error("Error fetching tutor by id:", e);
    }
  };

  // When tutorId is set but not found in tutors list, fetch it
  useEffect(() => {
    if (formData.tutorId && Array.isArray(tutors)) {
      const found = tutors.some(
        (t) => (t?._id || t?.id)?.toString() === formData.tutorId?.toString()
      );
      if (!found) {
        fetchTutorById(formData.tutorId);
      }
    }
  }, [formData.tutorId, tutors]);

  const fetchSlots = async () => {
    try {
      const response = await api.get("/slots");
      const raw = response?.data?.data;
      const slotsArr = Array.isArray(raw?.slots) ? raw.slots : Array.isArray(raw) ? raw : [];
      setSlots(slotsArr);
    } catch (error) {
      console.error("Error fetching slots:", error);
      setSlots([]);
      showToastsFromResponse(error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate student selection
    if (!formData.studentIds || formData.studentIds.length === 0) {
      newErrors.studentIds = "At least one student must be selected";
    }

    // Validate final preferred time slot
    if (!formData.preferredTimeSlots || formData.preferredTimeSlots.length === 0) {
      newErrors.preferredTimeSlots = "Final preferred time slot is required";
    }

    // Only validate fields required for status update
    if (!formData.demoStatus) {
      newErrors.demoStatus = "Status is required";
    }

    if (!formData.demoType) {
      newErrors.demoType = "Demo mode is required";
    }

    if (formData.demoStatus === "REJECTED" && !formData.rejectReason) {
      newErrors.rejectReason = "Reject reason is required when status is rejected";
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      Object.values(validationErrors).forEach((m) => m && toast.error(m));
      return;
    }

    setLoading(true);
    try {
      // Prepare data according to backend API structure
      const payload = {
        demoStatus: formData.demoStatus,
        // General editable fields
        date: formData.preferredDate ? dayjs(formData.preferredDate).format("YYYY-MM-DD") : null,
        mode: formData.demoType,
        address: formData.address,
        studentId: formData.studentIds,
        // meeting details
        preferredTimeSlots: formData.preferredTimeSlots,
        notes: formData.notes,
        tutorId: formData.tutorId,
        meetingLink: formData.meetingLink,
        // fee details
        perSessionFee: Number(formData.perSessionFee) || 0,
        monthlyFee: formData.monthlyFee,
      };
      if (formData.tutorByAdmin) payload.tutorByAdmin = formData.tutorByAdmin;
      if (formData.scheduledDate)
        payload.scheduledDate = dayjs(formData.scheduledDate).format("YYYY-MM-DD");

      if (formData.demoStatus === "REJECTED" && formData.rejectReason) {
        payload.rejectReason = formData.rejectReason;
      }

      const response = await api.put(`/demo/${id}`, payload);

      if (response.data.success) {
        showToastsFromResponse(response.data, "success");
        // Refresh details after update to ensure latest data shows
        await fetchDemoDetails();
        setTimeout(() => navigate("/demos"), 1000);
      } else {
        setErrors({ submit: response.data.message || "Failed to update demo" });
        showToastsFromResponse(response.data, "error");
      }
    } catch (error) {
      console.error("Error updating demo:", error);
      setErrors({ submit: "Network error. Please try again." });
      showToastsFromResponse(error, "error");
    } finally {
      setLoading(false);
    }
  };

  // Quick update status for simple transitions that don't need extra fields
  const quickUpdateStatuses = [
    "SELECTED",
    "REQUESTED",
    "SCHEDULED",
    "RESCHEDULED",
    "DONE",
    "CANCELLED",
  ];
  const handleStatusChange = async (newStatus) => {
    handleInputChange("demoStatus", newStatus);
    if (!quickUpdateStatuses.includes(newStatus)) return; // Require manual submit for REJECTED

    try {
      setStatusSaving(true);
      const payload = { demoStatus: newStatus };
      const response = await api.put(`/demo/${id}`, payload);
      if (!response?.data?.success) {
        setErrors({ submit: response?.data?.message || "Failed to update status" });
        showToastsFromResponse(response?.data, "error");
      } else {
        // Prefer server messages, fallback to status text
        if (response?.data) showToastsFromResponse(response.data, "success");
        else toast.success(`Status updated to ${newStatus}`);
      }
    } catch (err) {
      setErrors({ submit: "Network error while updating status" });
      showToastsFromResponse(err, "error");
    } finally {
      setStatusSaving(false);
    }
  };

  const demoTypes = [
    { value: "ONLINE", label: "Online" },
    { value: "OFFLINE", label: "Offline" },
    { value: "BOTH", label: "Both" },
  ];

  // Align with backend enum: ["SELECTED", "REQUESTED", "SCHEDULED", "RESCHEDULED", "DONE", "CANCELLED", "REJECTED"]
  const demoStatuses = [
    { value: "SELECTED", label: "Selected" },
    { value: "REQUESTED", label: "Requested" },
    { value: "SCHEDULED", label: "Scheduled" },
    { value: "RESCHEDULED", label: "Rescheduled" },
    { value: "DONE", label: "Completed" },
    { value: "CANCELLED", label: "Cancelled" },
    { value: "REJECTED", label: "Rejected" },
  ];

  if (initialLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox py={3}>
          <ArgonBox textAlign="center" py={4}>
            <ArgonTypography variant="h6" color="text" fontWeight="medium">
              Loading demo details...
            </ArgonTypography>
          </ArgonBox>
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  // Cancel Demo handler
  const handleCancelDemo = async () => {
    setStatusSaving(true);
    try {
      const payload = { demoStatus: "CANCELLED" };
      const response = await api.put(`/demo/${id}`, payload);
      if (response?.data?.success) {
        showToastsFromResponse("Demo cancelled successfully!", "success");
        // Use sessionStorage to trigger refresh in DemoList
        sessionStorage.setItem("refreshDemoList", Date.now().toString());
        setTimeout(() => navigate("/demos?tab=CANCELLED"), 500);
      } else {
        showToastsFromResponse(response?.data, "error");
      }
    } catch (err) {
      showToastsFromResponse(err, "error");
    } finally {
      setStatusSaving(false);
      setCancelDialogOpen(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <ArgonBox display="flex" alignItems="center" gap={2}>
                  <ArgonButton
                    variant="text"
                    color="secondary"
                    size="small"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/demos")}
                  >
                    Back to Demos
                  </ArgonButton>
                  <ArgonTypography variant="h6" fontWeight="medium">
                    Edit Demo
                  </ArgonTypography>
                </ArgonBox>
                {/* Cancel Demo Button: Only show if status is REQUESTED or SCHEDULED */}
                {(formData.demoStatus === "REQUESTED" || formData.demoStatus === "SCHEDULED") && (
                  <>
                    <ArgonButton
                      variant="contained"
                      color="error"
                      onClick={() => setCancelDialogOpen(true)}
                      disabled={statusSaving}
                    >
                      {statusSaving ? "Cancelling..." : "Cancel Demo"}
                    </ArgonButton>
                    <DeleteDialog
                      open={cancelDialogOpen}
                      onClose={() => setCancelDialogOpen(false)}
                      onConfirm={handleCancelDemo}
                      title="Cancel Demo?"
                      description="Are you sure you want to cancel this demo? This action cannot be undone."
                      confirmText={statusSaving ? "Cancelling..." : "Yes, Cancel Demo"}
                      cancelText="No, Keep Demo"
                    />
                  </>
                )}
              </ArgonBox>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* Student Selection */}
                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                        Select Students *
                      </ArgonTypography>
                      <Autocomplete
                        multiple
                        options={Array.isArray(students) ? students : []}
                        getOptionLabel={(option) => {
                          const displayName = option?.name || option?.fullName || "Unnamed";
                          const phone = option?.parentId?.phone || option?.phone || "";
                          return phone ? `${displayName} - ${phone}` : displayName;
                        }}
                        value={(Array.isArray(students) ? students : []).filter((student) =>
                          formData.studentIds.includes(student?._id || student?.id)
                        )}
                        onChange={(event, newValue) => {
                          const selectedIds = newValue.map(
                            (student) => student?._id || student?.id || ""
                          );
                          handleInputChange("studentIds", selectedIds);
                        }}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              variant="outlined"
                              label={option?.name || option?.fullName || "Unnamed"}
                              {...getTagProps({ index })}
                              key={option._id}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Search and select students"
                            error={!!errors.studentIds}
                            helperText={errors.studentIds}
                            size="small"
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <ArgonBox>
                              <ArgonTypography variant="button" fontWeight="medium">
                                {option?.name || option?.fullName || "Unnamed"}
                              </ArgonTypography>
                              <ArgonTypography variant="caption" color="secondary" display="block">
                                {option?.parentId?.phone || option?.phone || ""}{" "}
                                {option?.parentId?.email || option?.email
                                  ? `• ${option?.parentId?.email || option?.email}`
                                  : ""}
                              </ArgonTypography>
                            </ArgonBox>
                          </li>
                        )}
                      />
                    </ArgonBox>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                        Preferred Time Slots
                      </ArgonTypography>
                      <ArgonBox display="flex" flexWrap="wrap" gap={1}>
                        {(Array.isArray(formData.initialPreferredTimeSlots)
                          ? formData.initialPreferredTimeSlots
                          : []
                        ).map((slot) => (
                          <Chip
                            key={slot._id}
                            label={`${slot.startTime} - ${slot.endTime}`}
                            variant="outlined"
                            size="small"
                          />
                        ))}
                      </ArgonBox>
                    </ArgonBox>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                        Final Preferred Time Slot *
                      </ArgonTypography>
                      <Autocomplete
                        options={Array.isArray(slots) ? slots : []}
                        getOptionLabel={(option) => `${option.startTime} - ${option.endTime}`}
                        value={
                          Array.isArray(slots) && formData.preferredTimeSlots.length > 0
                            ? slots.find((slot) =>
                                formData.preferredTimeSlots.includes(
                                  slot?._id?.toString?.() || `${slot?._id}`
                                )
                              ) || null
                            : null
                        }
                        onChange={(event, newValue) => {
                          handleInputChange("preferredTimeSlots", newValue ? [newValue._id] : []);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Select final time slot"
                            error={!!errors.preferredTimeSlots}
                            helperText={errors.preferredTimeSlots}
                            size="small"
                          />
                        )}
                      />
                    </ArgonBox>
                  </Grid>

                  {/* Tutor Selection */}
                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                        Select Tutor *
                      </ArgonTypography>
                      <Autocomplete
                        options={Array.isArray(tutors) ? tutors : []}
                        getOptionLabel={(option) => `${option.fullName} - ${option.phone}`}
                        isOptionEqualToValue={(option, value) =>
                          (option?._id || option?.id)?.toString() ===
                          (value?._id || value?.id)?.toString()
                        }
                        value={
                          tutors.find(
                            (t) => t?._id === formData.tutorId || t?.id === formData.tutorId
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          handleInputChange("tutorId", newValue?._id || newValue?.id || "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Search and select tutor"
                            error={!!errors.tutorId}
                            helperText={errors.tutorId}
                            size="small"
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <ArgonBox>
                              <ArgonTypography variant="button" fontWeight="medium">
                                {option.fullName}
                              </ArgonTypography>
                              <ArgonTypography variant="caption" color="secondary" display="block">
                                {option.phone} • {option.email}
                              </ArgonTypography>
                            </ArgonBox>
                          </li>
                        )}
                      />
                    </ArgonBox>
                  </Grid>

                  {/* Assigned Admin
                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                        Assigned Admin
                      </ArgonTypography>
                      <ArgonTypography variant="button" fontWeight="medium" sx={{ mb: 0.25 }}>
                        {currentUser?.fullName || "Super Admin"}
                      </ArgonTypography>
                      <ArgonTypography variant="caption" color="secondary">
                        {currentUser?.email || "No email"}
                      </ArgonTypography>
                    </ArgonBox>
                  </Grid> */}

                  {/* Preferred Date */}
                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                        Final Date *
                      </ArgonTypography>
                      <CustomDatePicker
                        value={formData.preferredDate}
                        onChange={(newValue) => handleInputChange("preferredDate", newValue)}
                        width="100%"
                        minDate={dayjs()}
                        format="DD/MM/YYYY"
                      />
                      {errors.preferredDate && (
                        <ArgonTypography variant="caption" color="error" mt={0.5}>
                          {errors.preferredDate}
                        </ArgonTypography>
                      )}
                    </ArgonBox>
                  </Grid>

                  {/* Demo Status */}
                  {/* <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                        Demo Status *
                      </ArgonTypography>
                      <Autocomplete
                        options={demoStatuses}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        value={demoStatuses.find((o) => o.value === formData.demoStatus) || null}
                        onChange={(event, newValue) => {
                          const v = newValue?.value || "";
                          handleStatusChange(v);
                        }}
                        disabled={true}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Search and select status"
                            error={!!errors.demoStatus}
                            helperText={errors.demoStatus}
                            size="small"
                          />
                        )}
                      />

                      {statusSaving && (
                        <ArgonTypography variant="caption" color="text" mt={0.5} display="block">
                          Saving status...
                        </ArgonTypography>
                      )}
                    </ArgonBox>
                  </Grid> */}

                  {/* Demo Mode */}
                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                        Demo Mode *
                      </ArgonTypography>
                      <Autocomplete
                        options={demoTypes}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        value={demoTypes.find((o) => o.value === formData.demoType) || null}
                        onChange={(event, newValue) => {
                          handleInputChange("demoType", newValue?.value || "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Select or type demo mode"
                            error={!!errors.demoType}
                            helperText={errors.demoType}
                            size="small"
                          />
                        )}
                      />
                    </ArgonBox>
                  </Grid>

                  {/* Online specific fields */}
                  {(formData.demoType === "ONLINE" || formData.demoType === "BOTH") && (
                    <>
                      <Grid item xs={12} md={6}>
                        <ArgonBox mb={2}>
                          <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                            Meeting Link {formData.demoStatus === "APPROVED" ? "*" : ""}
                          </ArgonTypography>
                          <ArgonInput
                            type="url"
                            value={formData.meetingLink}
                            onChange={(e) => handleInputChange("meetingLink", e.target.value)}
                            placeholder="Enter online meeting link"
                            error={!!errors.meetingLink}
                            helperText={errors.meetingLink}
                          />
                        </ArgonBox>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <ArgonBox mb={2}>
                          <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                            Meeting Schedule Date
                          </ArgonTypography>
                          <CustomDatePicker
                            value={formData.scheduledDate}
                            onChange={(newValue) => handleInputChange("scheduledDate", newValue)}
                            width="100%"
                            minDate={dayjs()}
                            format="DD/MM/YYYY"
                          />
                          <ArgonTypography variant="caption" color="text" mt={0.5}>
                            Note: Time will be set based on selected time slots
                          </ArgonTypography>
                        </ArgonBox>
                      </Grid>
                    </>
                  )}

                  {/* Address (for offline demos) */}
                  {(formData.demoType === "OFFLINE" || formData.demoType === "BOTH") && (
                    <Grid item xs={12}>
                      <ArgonBox mb={2}>
                        <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                          Address *
                        </ArgonTypography>
                        <ArgonInput
                          multiline
                          rows={3}
                          value={formData.address}
                          onChange={(e) => handleInputChange("address", e.target.value)}
                          placeholder="Enter full address for offline demo"
                          error={!!errors.address}
                          helperText={errors.address}
                        />
                      </ArgonBox>
                    </Grid>
                  )}

                  {/* Reject Reason (for rejected demos) */}
                  {formData.demoStatus === "REJECTED" && (
                    <Grid item xs={12}>
                      <ArgonBox mb={2}>
                        <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                          Reject Reason *
                        </ArgonTypography>
                        <ArgonInput
                          multiline
                          rows={3}
                          value={formData.rejectReason}
                          onChange={(e) => handleInputChange("rejectReason", e.target.value)}
                          placeholder="Enter reason for rejection"
                          error={!!errors.rejectReason}
                          helperText={errors.rejectReason}
                        />
                      </ArgonBox>
                    </Grid>
                  )}

                  {/* Fee Information */}
                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                        Per Session Fee (₹)
                      </ArgonTypography>
                      <ArgonInput
                        type="text"
                        value={formData.perSessionFee}
                        onChange={(e) =>
                          handleInputChange("perSessionFee", parseFloat(e.target.value) || 0)
                        }
                        placeholder="Enter per session fee"
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      />
                    </ArgonBox>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                        Monthly Fee - 24 classes (₹)
                      </ArgonTypography>
                      <ArgonInput
                        type="text"
                        value={formData.monthlyFee}
                        onChange={(e) => handleInputChange("monthlyFee", e.target.value)}
                        placeholder="Enter monthly fee range for 24 classes"
                      />
                      <ArgonTypography variant="caption" color="error" mt={0.5}>
                        Example: 3000 - 5000
                      </ArgonTypography>
                    </ArgonBox>
                  </Grid>

                  {/* Notes */}
                  <Grid item xs={12}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                        Notes (Optional)
                      </ArgonTypography>
                      <ArgonInput
                        multiline
                        rows={3}
                        value={formData.notes}
                        onChange={(e) => handleInputChange("notes", e.target.value)}
                        placeholder="Add any additional notes or requirements"
                      />
                    </ArgonBox>
                  </Grid>

                  {/* Submit Error */}
                  {errors.submit && (
                    <Grid item xs={12}>
                      <ArgonBox mb={2}>
                        <ArgonTypography variant="caption" color="error">
                          {errors.submit}
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>
                  )}

                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <ArgonBox display="flex" justifyContent="flex-end" gap={2}>
                      <ArgonButton
                        variant="outlined"
                        color="secondary"
                        onClick={() => navigate("/demos")}
                        disabled={loading}
                      >
                        Cancel
                      </ArgonButton>
                      <ArgonButton
                        type="submit"
                        variant="gradient"
                        color="info"
                        startIcon={<SaveIcon />}
                        disabled={loading}
                      >
                        {loading ? "Updating..." : "Update Demo"}
                      </ArgonButton>
                    </ArgonBox>
                  </Grid>
                </Grid>
              </form>
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EditDemo;
